import {EventApi, EventInput} from '@fullcalendar/core';
import moment from 'moment';
import {momentToDateTime} from 'webapp-lib/utils';

import {TimeEntryParams, TimeEntryResponse} from '@lexian/api/timetracker-api';

import {
  EventApiWithTimEntryProps,
  TimeEntry,
  TimeEntryCalendarEventExtendedProps,
  TimeEntryCasus,
} from '../timetracker.types';

function apiCasusToTimeEntryCasus(
  casus: TimeEntryResponse['casus']
): TimeEntryCasus | undefined {
  return casus
    ? {id: casus.id, displayName: casus.displayNameWithClient}
    : undefined;
}

/**
 * Converts a time entry returned by the backend into a FullCalendar event.
 */
export function apiEntryToEventInput(entry: TimeEntryResponse): EventInput {
  const extendedProps: TimeEntryCalendarEventExtendedProps = {
    casus: apiCasusToTimeEntryCasus(entry.casus),
    description: entry.description,
    ongoing: entry.ongoing,
    billability: entry.billability,
    mutable: entry.mutable,
  };

  const start = moment(entry.startTime).toDate();
  const end = moment
    .max(
      !entry.ongoing ? moment(entry.endTime) : moment(new Date()),
      moment(start).add(1, 'minute')
    )
    .toDate();

  return {
    id: entry.id,
    title: entry.title,
    start,
    end,
    editable: entry.mutable,
    ...(entry.ongoing ? {startEditable: false} : {}),
    extendedProps,
  };
}

/**
 * Builds the request params from a live FullCalendar event (used for
 * drag/resize where the source is the calendar event).
 */
export function eventToParams(
  event: EventApiWithTimEntryProps | EventApi,
  overrides: Partial<TimeEntryParams> = {}
): TimeEntryParams {
  const extendedProps =
    event.extendedProps as TimeEntryCalendarEventExtendedProps;

  return {
    casusId: extendedProps.casus?.id,
    title: event.title,
    description: extendedProps.description,
    startTime: momentToDateTime(moment(event.start)),
    endTime: extendedProps.ongoing
      ? undefined
      : momentToDateTime(moment(event.end)),
    ...overrides,
  };
}

/**
 * Builds the request params from the frontend TimeEntry model produced by the
 * edit dialog. `ongoing` is supplied by the caller (preserved for an existing
 * entry, `false` for a freshly created one);
 */
export function timeEntryToParams(
  entry: TimeEntry,
  ongoing: boolean
): TimeEntryParams {
  return {
    casusId: entry.casus?.id,
    title: entry.title,
    description: entry.description,
    startTime: momentToDateTime(moment(entry.start)),
    endTime: ongoing ? undefined : momentToDateTime(moment(entry.end)),
  };
}
